// IndexedDB utility for storing and retrieving survey data
const DB_NAME = "SmartBEADatabase";
const DB_VERSION = 1;
const SURVEY_STORE = "surveyData";

interface SurveyDataRecord {
  id?: number;
  email: string;
  surveyData: any;
  timestamp: string;
}

// Initialize the database
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error("Error opening IndexedDB"));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = () => {
      const db = request.result;

      // Create object store for survey data
      if (!db.objectStoreNames.contains(SURVEY_STORE)) {
        const store = db.createObjectStore(SURVEY_STORE, {
          keyPath: "id",
          autoIncrement: true,
        });

        // Create an index on email for quick lookups
        store.createIndex("email", "email", { unique: false });
        store.createIndex("timestamp", "timestamp", { unique: false });
      }
    };
  });
};

// Save survey data to IndexedDB
export const saveSurveyData = async (data: SurveyDataRecord): Promise<number> => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SURVEY_STORE], "readwrite");
    const store = transaction.objectStore(SURVEY_STORE);

    const request = store.add(data);

    request.onsuccess = () => {
      resolve(request.result as number);
    };

    request.onerror = () => {
      reject(new Error("Error saving survey data to IndexedDB"));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

// Get survey data by email
export const getSurveyDataByEmail = async (email: string): Promise<SurveyDataRecord | null> => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SURVEY_STORE], "readonly");
    const store = transaction.objectStore(SURVEY_STORE);
    const index = store.index("email");

    // Get the most recent entry for this email
    const request = index.openCursor(IDBKeyRange.only(email), "prev");

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        resolve(cursor.value);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      reject(new Error("Error retrieving survey data from IndexedDB"));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

// Delete survey data by email
export const deleteSurveyDataByEmail = async (email: string): Promise<boolean> => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SURVEY_STORE], "readwrite");
    const store = transaction.objectStore(SURVEY_STORE);
    const index = store.index("email");

    const request = index.openCursor(IDBKeyRange.only(email));

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    transaction.oncomplete = () => {
      db.close();
      resolve(true);
    };

    transaction.onerror = () => {
      reject(new Error("Error deleting survey data from IndexedDB"));
    };
  });
};

// Get all survey data
export const getAllSurveyData = async (): Promise<SurveyDataRecord[]> => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SURVEY_STORE], "readonly");
    const store = transaction.objectStore(SURVEY_STORE);

    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(new Error("Error retrieving all survey data from IndexedDB"));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};
